import Anchor from "atoms/anchors/anchor";
import Button from "atoms/buttons/button";
import { ButtonSizes } from "atoms/constants/button-sizes";
import { IconSizes } from "atoms/constants/icon-sizes";
import { Icons } from "atoms/constants/icons";
import Icon from "atoms/icons/icon";
import { siteMap } from "internal-sitemap";
import FileRecord from "models/view-models/file-record";
import PublicationRecord from "models/view-models/publication-record";
import UserPublicationFavoriteRecord from "models/view-models/user-publication-favorite-record";
import PublicationOptionsMenu from "organisms/modals/publication-selection-modal/publication-options-menu";
import { PublicationSelectionListProps } from "organisms/modals/publication-selection-modal/publication-selection-list";
import ReadonlyPublicationInfoModal from "organisms/modals/readonly-publication-info-modal";
import * as React from "react";
import { Fragment, useState } from "react";
import { useGlobalState } from "utilities/contexts/use-global-state-context";
import publicationContentFormatType from "utilities/enumerations/publication-content-format-type";
import useUpdatePublicationFavorite from "utilities/hooks/domain/user-publication-favorites/use-update-publication-favorite";
import { useLocalization } from "utilities/hooks/use-localization";
import { RouteUtils } from "utilities/route-utils";
import StringUtils from "utilities/string-utils";

// -----------------------------------------------------------------------------------------
// #region Interfaces
// -----------------------------------------------------------------------------------------

export enum PublicationSelectionListGroupIteStyle {
    EditionFirst = "-edition-first",
    TitleFirst = "-title-first",
}

interface PublicationSelectionListGroupItemProps
    extends Pick<PublicationSelectionListProps, "displayMenuActions"> {
    coverImageFile?: FileRecord;
    favorites?: Array<UserPublicationFavoriteRecord>;
    onChangeFavorites: (newValue: Array<UserPublicationFavoriteRecord>) => void;
    publication: PublicationRecord;
    style?: PublicationSelectionListGroupIteStyle;
    onResultClick?: Function;
}

// #endregion Interfaces

// -----------------------------------------------------------------------------------------
// #region Constants
// -----------------------------------------------------------------------------------------

const BASE_CLASS = "c-publication-selection-list-group-item";

// #endregion Constants

// -----------------------------------------------------------------------------------------
// #region Component
// -----------------------------------------------------------------------------------------

const PublicationSelectionListGroupItem = (
    props: PublicationSelectionListGroupItemProps
) => {
    const {
        coverImageFile,
        displayMenuActions = true,
        favorites = [],
        publication,
        onChangeFavorites,
        style = PublicationSelectionListGroupIteStyle.EditionFirst,
        onResultClick = () => {},
    } = props;

    const { globalState } = useGlobalState();
    const { currentIdentity } = globalState;
    const { t } = useLocalization();

    const coverImageSrc =
        coverImageFile?.presignedUrl ??
        publication.coverImageFile?.presignedUrl;

    const [isFocused, setIsFocused] = useState(false);

    const { isFavorited } = useUpdatePublicationFavorite(
        publication,
        favorites,
        onChangeFavorites
    );

    const coverStyle: React.CSSProperties = StringUtils.hasValue(coverImageSrc)
        ? {
              backgroundImage: `url(${coverImageSrc})`,
              backgroundSize: "cover",
          }
        : {};

    const modifierClasses: string[] = [];

    if (isFavorited) {
        modifierClasses.push(" -favorite");
    }

    if (isFocused) {
        modifierClasses.push(" -focused");
    }

    const isSubscriberOrInternalUser =
        currentIdentity?.isConfiguredWithActiveSubscription() ||
        currentIdentity?.isAdmin() ||
        currentIdentity?.isAuthorOrPublisher() ||
        currentIdentity?.isOrganizationMember();
    const publicationRoute = isSubscriberOrInternalUser
        ? publication.getRoute()
        : RouteUtils.getUrl(siteMap.publications.public.preview, {
              code: publication.code,
              edition: publication.edition,
          });

    const [showLegacyInfoModal, setShowLegacyInfoModal] =
        useState<boolean>(false);

    return (
        <div
            className={`${BASE_CLASS} ${modifierClasses.join(" ")}`}
            onBlurCapture={() => setIsFocused(false)}
            onFocusCapture={() => setIsFocused(true)}>
            <div className={`${BASE_CLASS}__item_parent`}>
                <Anchor
                    onClick={(e) => onResultClick()}
                    cssClassName={`${BASE_CLASS}__item ${style.toString()}`}
                    to={publicationRoute}>
                    <div
                        className={`${BASE_CLASS}__cover`}
                        style={coverStyle}
                    />
                    <div>
                        <PublicationText
                            publication={publication}
                            style={style}
                        />
                    </div>
                </Anchor>
                {displayMenuActions && (
                    <>
                        <div className={`${BASE_CLASS}__actions`}>
                            {publication.publicationContentFormatType ===
                                publicationContentFormatType.Image && (
                                <Button
                                    accessibleText={t(
                                        "readonlyPublicationAboutTitle"
                                    )}
                                    onClick={() => setShowLegacyInfoModal(true)}
                                    cssClassName={"c-action-button"}
                                    size={ButtonSizes.Small}>
                                    <Icon
                                        cssClassName="c-legacy-icon"
                                        type={Icons.Scroll}
                                        size={IconSizes.Base}
                                    />
                                    <ReadonlyPublicationInfoModal
                                        isVisible={showLegacyInfoModal}
                                        closeDialog={() =>
                                            setShowLegacyInfoModal(false)
                                        }
                                    />
                                </Button>
                            )}

                            <PublicationOptionsMenu
                                favorites={favorites}
                                onChangeFavorites={onChangeFavorites}
                                publication={publication}
                            />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

// #endregion Component

// -----------------------------------------------------------------------------------------
// #region Functions
// -----------------------------------------------------------------------------------------

function PublicationText(
    props: Pick<PublicationSelectionListGroupItemProps, "publication" | "style">
) {
    const { publication, style } = props;

    const edition = (
        <div className={`${BASE_CLASS}__item__edition`}>
            {publication.edition}
        </div>
    );

    const title = (
        <div className={`${BASE_CLASS}__item__title`}>
            {publication.getDisplayTitle(false)}
        </div>
    );

    if (style === PublicationSelectionListGroupIteStyle.EditionFirst) {
        return (
            <Fragment>
                {edition}
                {title}
            </Fragment>
        );
    }

    return (
        <Fragment>
            {title}
            {edition}
        </Fragment>
    );
}

// #endregion Functions

// -----------------------------------------------------------------------------------------
// #region Exports
// -----------------------------------------------------------------------------------------

export default PublicationSelectionListGroupItem;

// #endregion Exports
